import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import Sidebar from '../custom/Sidebar';
import { AuthProvider, useAuth } from '../../context/AuthContext';
import { PermissionsProvider } from '../../context/PermissionsContext';
import TenantSelectModal, { TenantSelectBackdrop } from './TenantSelectModal';
import api from '../../services/axios';
import {
  checkAuthSession,
  clearProtectedShellPath,
  clearSsoAuthPending,
  confirmTenantSelection,
  installBfCacheBlocker,
  isSessionEndedLocally,
  isSsoAuthPending,
  isTenantSelectionConfirmed,
  postLoginTarget,
  setProtectedShellPath,
} from '../../utils/authSession';
import { featureLabelForPath, isPathAllowed, PERMISSION_DENIED_MESSAGE } from '../../utils/entitlements';
import { openWizetokInNewTab } from '../../utils/crossAppSso';
import {
  canWrite,
  homePathForKind,
  installViewerWriteGuard,
  isPathAllowedForRole,
  sessionCompanyKind,
} from '../../utils/roleAccess';

const PAGE_NAMES = {
  '/analytics': 'Analytics Overview',
  '/open-positions': 'Craft JD',
  '/existing-positions': 'Stack Ranking',
  '/stack-ranking': 'Stack Ranking',
  '/resume-formatter': 'Format Resume',
  '/engagement': 'ReachOut / Engagement',
  '/whatsapp-reachout': 'ReachOut / Engagement',
  '/database': 'Database',
  '/jd-preview': 'JD Preview',
  '/set-password': 'Set Password',
  '/candidates': 'Candidates',
  '/open-wizetok': 'Open Wizetok',
  '/admin': 'Administration',
};

function getPageName(pathname) {
  for (const [prefix, name] of Object.entries(PAGE_NAMES)) {
    if (pathname === prefix || pathname.startsWith(prefix + '/')) return name;
  }
  return 'Dashboard';
}

function AuthGateBlank() {
  return <div className="auth-gate-screen" aria-busy="true" aria-hidden="true" />;
}

function AuthGateScreen({ message = 'Signing you in...' }) {
  return (
    <div className="auth-gate-screen loading-page" aria-live="polite" aria-busy="true">
      <div className="loading-spinner spinner-border text-secondary" role="status" />
      <div className="loading-text">{message}</div>
    </div>
  );
}

function isLegacyDefaultTenant(tenant) {
  const slug = String(tenant?.slug || '').trim().toLowerCase();
  const name = String(tenant?.name || '').trim().toLowerCase();
  return slug === 'default' || name === 'default';
}

function TenantSelectGate({ children }) {
  const { user, isLoading } = useAuth();
  const tenants = (Array.isArray(user?.availableTenants) ? user.availableTenants : [])
    .filter((tenant) => !isLegacyDefaultTenant(tenant));
  const needsPick = !isLoading && tenants.length > 1 && !isTenantSelectionConfirmed(user?.tenantId);
  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!needsPick) return;
    const current = user?.tenantId;
    const match = tenants.find((t) => String(t.id) === String(current));
    setSelectedId(match ? match.id : tenants[0]?.id ?? null);
    setError('');
  }, [needsPick, user?.tenantId, user?.availableTenants]);

  if (!needsPick) return children;

  const onContinue = async () => {
    if (selectedId == null || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/auth/select-tenant', { tenantId: selectedId });
      confirmTenantSelection(selectedId);
      clearProtectedShellPath();
      window.location.replace(await postLoginTarget());
    } catch {
      setError('Could not switch tenant. Try again.');
      setSubmitting(false);
    }
  };

  return (
    <>
      <TenantSelectBackdrop />
      <TenantSelectModal
        tenants={tenants}
        selectedId={selectedId}
        onChange={setSelectedId}
        onContinue={onContinue}
        submitting={submitting}
        error={error}
      />
    </>
  );
}

function FeatureOutlet() {
  const location = useLocation();
  const { entitlements, unrestricted, isLoading, role, tenantRole, tenantRoles } = useAuth();
  const kind = sessionCompanyKind(tenantRole, role, unrestricted, tenantRoles);
  if (isLoading) return <Outlet />;
  if (!isPathAllowedForRole(location.pathname, kind)) {
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '40px 24px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            padding: '36px 40px',
            borderRadius: 16,
            border: '1px solid #FECACA',
            background: '#FEF2F2',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, color: '#B91C1C', marginBottom: 10 }}>
            {PERMISSION_DENIED_MESSAGE}
          </div>
          <div style={{ fontSize: 14, color: '#7F1D1D', lineHeight: 1.5 }}>
            This page is not available for your role.
          </div>
        </div>
      </div>
    );
  }
  if (!isPathAllowed(location.pathname, entitlements, { unrestricted })) {
    const featureName = featureLabelForPath(location.pathname);
    return (
      <div
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: '40px 24px',
        }}
      >
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            padding: '36px 40px',
            borderRadius: 16,
            border: '1px solid #FECACA',
            background: '#FEF2F2',
            textAlign: 'center',
          }}
        >
          <div style={{ fontSize: 18, fontWeight: 700, color: '#B91C1C', marginBottom: 10 }}>
            {featureName} is currently unavailable.
          </div>
          <div style={{ fontSize: 14, color: '#7F1D1D', lineHeight: 1.5 }}>
            Please contact your Service Provider to enable access to this feature.
          </div>
        </div>
      </div>
    );
  }
  if (location.pathname === '/candidates' || location.pathname.startsWith('/candidates/')) {
    return <Navigate to="/engagement" replace />;
  }
  if (location.pathname === '/open-wizetok' || location.pathname.startsWith('/open-wizetok/')) {
    return <OpenWizetokRedirect />;
  }
  return <Outlet />;
}

function OpenWizetokRedirect() {
  const navigate = useNavigate();
  useEffect(() => {
    openWizetokInNewTab();
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/open-positions', { replace: true });
    }
  }, [navigate]);
  return null;
}

function RoleShell({ children, theme, collapsed, onToggleCollapse, onThemeToggle, location, navigate, currentPage }) {
  const { role, tenantRole, tenantRoles, unrestricted } = useAuth();
  const kind = sessionCompanyKind(tenantRole, role, unrestricted, tenantRoles);
  const homePath = homePathForKind(kind);
  const readOnly = !canWrite(kind);
  const isDark = theme === 'dark';
  const topbarBg = isDark ? 'rgba(13,17,23,0.97)' : 'rgba(255,255,255,0.97)';
  const topbarBorder = isDark ? 'rgba(0,65,194,0.18)' : '#E4E8F0';
  const contentBg = isDark ? '#111827' : '#F2F4F8';
  const breadText = isDark ? '#EEF2FF' : '#0D1B3E';
  const breadMuted = isDark ? '#A8B4D8' : '#6B7280';
  const breadSep = isDark ? 'rgba(255,255,255,0.2)' : '#C8D0E0';
  const btnBorder = isDark ? 'rgba(0,65,194,0.35)' : '#D0D7E8';
  const btnColor = isDark ? '#A8B4D8' : '#4A5568';

  useEffect(() => {
    if (!readOnly) return undefined;
    return installViewerWriteGuard();
  }, [readOnly]);

  return (
    <div
      className="app-shell"
      data-role-readonly={readOnly ? 'true' : undefined}
      style={{
        fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
        background: contentBg,
        transition: 'background 0.25s',
      }}
    >
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={onToggleCollapse}
        theme={theme}
        onThemeToggle={onThemeToggle}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          overflowX: 'hidden',
          overscrollBehavior: 'none',
          position: 'relative',
          zIndex: 1,
          minWidth: 0,
          maxWidth: '100%',
        }}
      >
        <div
          style={{
            height: 52,
            background: topbarBg,
            backdropFilter: 'blur(12px)',
            borderBottom: `1px solid ${topbarBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 28px',
            flexShrink: 0,
            transition: 'background 0.25s, border-color 0.25s',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: breadMuted }}>
            <span
              onClick={() => {
                if (location.pathname !== homePath) navigate(homePath);
              }}
              style={{ fontFamily: "'Plus Jakarta Sans', sans-serif", cursor: 'pointer', transition: 'color 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.color = '#1A5FD4'; }}
              onMouseLeave={e => { e.currentTarget.style.color = ''; }}
            >
              ReachOut
            </span>
            <span style={{ color: breadSep }}>›</span>
            <span style={{ color: breadText, fontWeight: 600 }}>{currentPage}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative' }}>
              <button
                type="button"
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '6px 10px', borderRadius: 8,
                  border: `1px solid ${btnBorder}`, background: 'transparent',
                  color: btnColor, cursor: 'pointer', transition: 'all 0.18s',
                  outline: 'none',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#1A5FD4';
                  e.currentTarget.style.color = '#1A5FD4';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = btnBorder;
                  e.currentTarget.style.color = btnColor;
                }}
              >
                <Bell size={16} />
              </button>
              <div
                style={{
                  position: 'absolute', top: 5, right: 5,
                  width: 5, height: 5, background: '#E040FB',
                  borderRadius: '50%',
                  boxShadow: isDark ? '0 0 5px #E040FB' : 'none',
                }}
              />
            </div>
          </div>
        </div>
        <div
          key={`${location.pathname}::${location.state?.navReload || location.state?._refresh || ''}`}
          className="app-main-content"
          style={{
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'none',
            color: breadText,
            transition: 'color 0.25s',
            backgroundColor: contentBg,
            display: 'flex',
            flexDirection: 'column',
            maxWidth: '100%',
          }}
        >
          <div style={{ flex: 1, padding: '20px 24px 0' }}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ProtectedLayout() {
  const location = useLocation();
  const navigate = useNavigate();

  const [status, setStatus] = useState(() =>
    isSsoAuthPending() ? 'checking' : (isSessionEndedLocally() ? 'unauthed' : 'checking')
  );
  const [collapsed, setCollapsed] = useState(false);
  const [theme, setTheme] = useState(() => {
    try { return localStorage.getItem('hirecrux-theme') || 'light'; } catch { return 'light'; }
  });


  // Global "New Position" dropdown + modal (commented out for now)
  // const [showNewPosDropdown, setShowNewPosDropdown] = useState(false);
  // const [showNewPositionModal, setShowNewPositionModal] = useState(false);
  // const [newPositionSource, setNewPositionSource] = useState('manual');
  // const [currentUser, setCurrentUser] = useState(null);
  // const newPosDropdownRef = useRef(null);

  // useEffect(() => {
  //   api.get('/auth/me').then(res => setCurrentUser(res.data)).catch(() => {});
  // }, []);

  // useEffect(() => {
  //   if (!showNewPosDropdown) return;
  //   const handler = (e) => {
  //     if (newPosDropdownRef.current && !newPosDropdownRef.current.contains(e.target)) {
  //       setShowNewPosDropdown(false);
  //     }
  //   };
  //   document.addEventListener('mousedown', handler);
  //   return () => document.removeEventListener('mousedown', handler);
  // }, [showNewPosDropdown]);

  // Persist + apply theme to html element so CSS vars take effect everywhere
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('hirecrux-theme', theme); } catch {}
  }, [theme]);

  const handleThemeToggle = () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'));

  // Auth validationâ”€â”€
  useLayoutEffect(() => {
    let alive = true;
    let intervalId;
    // Require 2 consecutive failures before logging out a previously-authed user -
    // prevents a single backend hiccup (restart, network blip) from kicking them out.
    // For a brand-new visitor who was never authed this session, redirect immediately
    // on the first 401 so they reach the login page without a 60-second blank wait.
    let consecutiveFailures = 0;
    const FAILURES_BEFORE_LOGOUT = 2;

    const validateSession = async () => {
      if (!alive) return;
      if (isSessionEndedLocally() && !isSsoAuthPending()) { setStatus('unauthed'); return; }

      const authed = await checkAuthSession({ ignoreEndedMarker: isSsoAuthPending() });
      if (!alive) return;

      if (authed) {
        consecutiveFailures = 0;
        clearSsoAuthPending();
        setStatus('authed');
        return;
      }

      if (isSsoAuthPending()) {
        for (let i = 0; i < 4; i++) {
          await new Promise((r) => setTimeout(r, 400));
          if (!alive) return;
          const retryAuthed = await checkAuthSession({ ignoreEndedMarker: true });
          if (retryAuthed) {
            clearSsoAuthPending();
            setStatus('authed');
            return;
          }
        }
        clearSsoAuthPending();
        if (!alive) return;
        setStatus('unauthed');
        return;
      }

      consecutiveFailures += 1;
      if (consecutiveFailures < FAILURES_BEFORE_LOGOUT) {
        await new Promise((r) => setTimeout(r, 400));
        if (alive) void validateSession();
        return;
      }
      setStatus('unauthed');
    };

    void validateSession();

    const onFocusOrVisibility = () => void validateSession();
    const onPageShow = () => {
      if (isSessionEndedLocally() && !isSsoAuthPending()) { setStatus('unauthed'); return; }
      if (isSsoAuthPending()) setStatus('checking');
      void validateSession();
    };
    const onPopState = () => {
      if (isSessionEndedLocally() && !isSsoAuthPending()) { setStatus('unauthed'); return; }
      if (isSsoAuthPending()) setStatus('checking');
      void validateSession();
    };

    window.addEventListener('focus', onFocusOrVisibility);
    document.addEventListener('visibilitychange', onFocusOrVisibility);
    window.addEventListener('online', onFocusOrVisibility);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('popstate', onPopState);
    // Poll every 60s instead of 15s - reduces unnecessary /auth/me calls and gives
    // the backend more time to recover from a restart before users get kicked out.
    intervalId = window.setInterval(() => void validateSession(), 60000);

    return () => {
      alive = false;
      if (intervalId) window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocusOrVisibility);
      document.removeEventListener('visibilitychange', onFocusOrVisibility);
      window.removeEventListener('online', onFocusOrVisibility);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('popstate', onPopState);
    };
  }, [location.pathname, location.search, location.key]);

  useEffect(() => {
    if (status !== 'authed') return undefined;
    return installBfCacheBlocker();
  }, [status]);

  // Remember current page so Chrome swipe-back can restore it (stored in sessionStorage too).
  useEffect(() => {
    if (status !== 'authed') return;
    setProtectedShellPath(`${location.pathname}${location.search}`);
  }, [status, location.pathname, location.search]);

  // Chrome trackpad: block horizontal swipe that triggers browser back/forward.
  useEffect(() => {
    if (status !== 'authed') return undefined;

    const blockHorizontalSwipe = (e) => {
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY) && Math.abs(e.deltaX) > 8) {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', blockHorizontalSwipe, { passive: false, capture: true });
    return () => window.removeEventListener('wheel', blockHorizontalSwipe, { capture: true });
  }, [status]);

  // Auth gate renders
  if (isSessionEndedLocally() && !isSsoAuthPending()) return <Navigate to="/login" replace />;
  if (status === 'checking') return isSsoAuthPending() ? <AuthGateScreen /> : <AuthGateBlank />;
  if (status === 'unauthed') return <Navigate to="/login" replace state={{ from: location }} />;

  const currentPage = getPageName(location.pathname);

  return (
    <AuthProvider>
      <TenantSelectGate>
      <PermissionsProvider>
      <RoleShell
        theme={theme}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onThemeToggle={handleThemeToggle}
        location={location}
        navigate={navigate}
        currentPage={currentPage}
      >
        <FeatureOutlet />
      </RoleShell>
      </PermissionsProvider>
      </TenantSelectGate>
    </AuthProvider>
  );
}
